import { tellerPoolsByChain } from '@1delta/data-sdk'
import { LenderCommitmentGroupAbi } from '@1delta/abis'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import { tellerLenderKey } from '../../../lending/public-data/teller/convertPublic'

/**
 * Teller Protocol V2 oracle price fetcher.
 *
 * Each `LenderCommitmentGroup` pool carries its OWN price mechanism (a Uniswap
 * V3 TWAP / Chainlink feed configured per pool) that values the collateral in
 * principal-token terms — the same price the pool uses to size a borrow's
 * collateral. We read `calculateCollateralTokensAmountEquivalentToPrincipalTokens`
 * (the raw value ratio, WITHOUT the overcollateralization factor) to get "how
 * much collateral equals 1 principal token in value", then invert to the
 * collateral price in principal terms.
 *
 * Why a dedicated fetcher: Teller pools list EXOTIC collateral no other protocol
 * on the chain prices (e.g. VCNT, LFI, DN-FLAGSHIP). Without this, those markets
 * would have no oracle price and their USD/risk numbers would be blank or wrong.
 * For the standard tokens (WETH/USDC/AERO) the generic pipeline already has a
 * price; this adds the exotic ones from Teller's OWN oracle — the correct source
 * for that market.
 *
 * Like Morpho/Midnight, Teller collateral prices are DERIVED: the collateral USD
 * needs the PRINCIPAL token's USD price from an earlier (Pass 1) parser. Parsed
 * with `updatePrices=false` so derived values never overwrite direct oracle reads.
 */

/** Grouped metadata for one pool (one equivalence call). */
interface TellerPoolMeta {
  pool: string
  principal: string
  principalDecimals: number
  collateral: string
  collateralDecimals: number
}

/**
 * Build multicall calls for Teller oracles. One `FetcherResult` per curated pool
 * with a single call: the collateral amount whose value equals ONE principal
 * token. `lender` is the pool's `TELLER_<pool>` key so entries join the lending
 * data emitted by `convertTellerMarketsToResponse`.
 */
export function getTellerCalls(
  chainId: string,
): FetcherResult<TellerPoolMeta>[] {
  const pools = tellerPoolsByChain(chainId)
  if (pools.length === 0) return []

  const results: FetcherResult<TellerPoolMeta>[] = []
  for (const p of pools) {
    if (!p.pool || !p.principal || !p.collateral) continue
    const onePrincipal = 10n ** BigInt(p.principalDecimals)
    const calls: MulticallCall[] = [
      {
        address: p.pool,
        name: 'calculateCollateralTokensAmountEquivalentToPrincipalTokens',
        params: [onePrincipal],
      },
    ]
    results.push({
      calls,
      meta: {
        pool: p.pool.toLowerCase(),
        principal: p.principal.toLowerCase(),
        principalDecimals: p.principalDecimals,
        collateral: p.collateral.toLowerCase(),
        collateralDecimals: p.collateralDecimals,
      },
      lender: tellerLenderKey(p.pool),
    })
  }
  return results
}

/**
 * Parse Teller oracle results. `data[0]` = the collateral base units of equal
 * value to 1 principal token. The collateral price in principal terms is
 * `10^collateralDecimals / rawCollateralUnits`; USD = that × principal USD.
 * Emits a principal entry (price = 1, the oracle's own unit) + the collateral
 * entry (derived).
 */
export function parseTellerResults(
  data: any[],
  meta: TellerPoolMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId, usdPrices, tokenList } = context
  const entries: OraclePriceEntry[] = []

  const principal = meta.principal
  const collateral = meta.collateral
  const lenderKey = tellerLenderKey(meta.pool)

  const usdOf = (addr: string): number | undefined => {
    const key = tokenList?.[addr]?.assetGroup ?? `${chainId}-${addr}`
    return usdPrices[key] ?? usdPrices[addr]
  }
  const principalUSD = usdOf(principal)
  const collateralUSD = usdOf(collateral)

  // Value ratio from the pool's OWN oracle: `equivalent` collateral base units
  // are worth exactly 1 whole principal token (the fetcher passed 10^principalDec).
  const rawCollateral = data?.[0]
  if (rawCollateral == null || rawCollateral === '0x') return entries
  let equivalent: bigint
  try {
    equivalent = BigInt(rawCollateral)
  } catch {
    return entries
  }
  if (equivalent <= 0n) return entries
  const collateralDecimals =
    meta.collateralDecimals ?? tokenList?.[collateral]?.decimals ?? 18
  // Whole collateral tokens equal in value to 1 whole principal token.
  const collPerPrincipal = Number(equivalent) / 10 ** collateralDecimals
  if (!(collPerPrincipal > 0)) return entries

  // BIDIRECTIONAL: derive whichever side lacks a direct USD price from the side
  // that has one, using the pool oracle. This is essential because a Teller pool
  // often lists the EXOTIC token as the PRINCIPAL (e.g. borrow a memecoin
  // against USDC) — the known side is then the collateral, not the principal.
  if (principalUSD) {
    // Base = principal. collateral price (in principal) = 1 / collPerPrincipal.
    const collateralPriceInPrincipal = 1 / collPerPrincipal
    entries.push({
      asset: principal,
      price: 1,
      priceUSD: principalUSD,
      marketUid: createMarketUid(chainId, lenderKey, principal),
      targetLender: lenderKey,
      description: 'Teller principal asset',
      staticBase: true,
      baseAsset: principal,
    })
    entries.push({
      asset: collateral,
      price: collateralPriceInPrincipal,
      priceUSD: collateralPriceInPrincipal * principalUSD,
      marketUid: createMarketUid(chainId, lenderKey, collateral),
      targetLender: lenderKey,
      baseAsset: principal,
    })
  } else if (collateralUSD) {
    // Base = collateral (e.g. USDC). principal price (in collateral) = collPerPrincipal.
    entries.push({
      asset: collateral,
      price: 1,
      priceUSD: collateralUSD,
      marketUid: createMarketUid(chainId, lenderKey, collateral),
      targetLender: lenderKey,
      description: 'Teller collateral asset',
      staticBase: true,
      baseAsset: collateral,
    })
    entries.push({
      asset: principal,
      price: collPerPrincipal,
      priceUSD: collPerPrincipal * collateralUSD,
      marketUid: createMarketUid(chainId, lenderKey, principal),
      targetLender: lenderKey,
      baseAsset: collateral,
    })
  }
  // else: neither side has a base USD price → can't derive; emit nothing.

  return entries
}

/** ABI for the Teller oracle call (the pool's value-equivalence view). */
export function getTellerAbi() {
  return LenderCommitmentGroupAbi
}

/** Teller Oracle Fetcher module. */
export const tellerFetcher = {
  getCalls: getTellerCalls,
  parse: parseTellerResults,
  getAbi: getTellerAbi,
}
